/**
 * 📋 SDLC 6.5.0 UNIFIED SPECIFICATION STANDARD FILE HEADER
 * ===============================================================================
 * FILE: frontend/src/pages/manager/ManagerTasksPage.tsx
 * PURPOSE: BR-WF-003 Manager Task Assignment ("Giao việc") — Manager giao
 *          NHIỀU việc khác nhau cho NHIỀU NV, lặp lại trên NHIỀU ngày, trong
 *          1 lần Giao việc (modal tìm theo tên/Mã NV + nhóm Bộ phận + chọn
 *          nhiều); xem/duyệt/từ chối nhiệm vụ NV đã Xác nhận + zoom ảnh bằng
 *          chứng; trang Tổng hợp nhiệm vụ theo NV (ai làm nhiều/ít).
 * SDLC FRAMEWORK VERSION: 6.5.0 (7-Pillar + 9 Mental Models + Long-Running Agent Protocol)
 * DATE: 2026-10-05 (daysInRange/todayISO format the local calendar date, not
 *       the UTC one; in UTC+7 shift_dates were one day early).
 *       2026-09-14 (issue #891 fix — "Tổng hợp theo NV" column label
 *       "Chưa xác nhận" → "Chưa hoàn thành", matches what pending_confirm
 *       actually counts; page otherwise unchanged since #715 2026-09-02)
 * COMPLIANCE: SDLC 6.5.0 + Sprint Governance Rule 3 (Zero Mock Tolerance)
 * PILLAR: P2 (Sprint Governance) + P4 (Quality Gates) + P6 (UI/UX Design)
 * SPRINT GOVERNANCE: Rule 3: Zero-Mock Tolerance
 *
 * @business-context
 * - Domain: Vietnamese F&B Workforce Management — Manager giao việc cho NV
 * - Impact: Revenue Critical — vận hành hàng ngày tại chi nhánh
 * - Stakeholders: Branch/Shift Managers, Employees
 *
 * @technical-architecture
 * - Layer: Presentation Layer (Page Component)
 * - Pattern: TanStack Query v5, CSS Module (--nqh-* design tokens)
 * - Dependencies: useTaskRoster, useBulkCreateTasks, usePendingApprovalTasks,
 *   useApproveTask, useEmployeeTaskSummary
 *
 * @cross-references
 * - Hooks: frontend/src/hooks/useManagerTasks.ts
 * - API: frontend/src/services/api/managerTasksApi.ts
 * - Backend: backend/app/api/v2/tasks.py
 * - Route: frontend/src/App.tsx (/manager/tasks)
 * - Design: docs/01-planning/01-requirements/09-workflows/BR-WF-003-manager-task-assignment.md
 * - Issue: GitHub #715 (BOT-004(a), P1) — bulkCreate/approveTask/
 *   bulkApproveTasks had no onError and no call site had try/catch or
 *   rendered `.isError` — a failed write (network/422/500) produced zero
 *   user-visible feedback.
 * - Issue: GitHub #891 (bot-071) — "Chưa xác nhận" column didn't decrease on
 *   Employee "Đã nhận" because pending_confirm counts ASSIGNED/ACKNOWLEDGED/
 *   IN_PROGRESS/OVERDUE (not-yet-reported-done), not not-yet-acknowledged;
 *   label-only fix, backing count unchanged (see backend/app/api/v2/tasks.py).
 * - Test: frontend/tests/pages/manager/ManagerTasksPage.silentFailure.test.tsx
 * ===============================================================================
 */

import React, { useMemo, useState } from "react";
import {
  useApproveTask,
  useBulkApproveTasks,
  useBulkCreateTasks,
  useEmployeeTaskSummary,
  usePendingApprovalTasks,
  useTaskHistory,
  useTaskRoster,
} from "../../hooks/useManagerTasks";
import {
  parseTaskPhotos,
  type BulkTaskItemPayload,
  type DeadlineMarker,
  type TaskHistoryItem,
  type TaskRosterItem,
} from "../../services/api/managerTasksApi";
import { DateRangePicker, type DateRangeValue } from "../../components/ui/DateRangePicker";
import { TimeTextInput } from "../../components/ui/TimeTextInput";
import { toLocalISODate } from "../../utils/date";
import styles from "./ManagerTasksPage.module.css";

const MARKER_OPTIONS: { value: DeadlineMarker; label: string }[] = [
  { value: "start", label: "Đầu ca" },
  { value: "mid", label: "Giữa ca" },
  { value: "end", label: "Kết ca" },
  // 2026-08-18 (theo yêu cầu người dùng): "việc phát sinh" không khớp khung
  // Đầu/Giữa/Kết ca — cho Quản lý tự nhập 1 giờ hạn chót cụ thể.
  { value: "custom", label: "Theo thời gian" },
];

// BR-WF-003 §backend BulkCreateTaskRequest.shift_dates: max_length=31.
const MAX_GIAO_VIEC_SPAN_DAYS = 31;

// Calendar day as the browser's local (VN) date. toISOString() converts to UTC
// first, which in UTC+7 gives the previous day for local midnight..07:00.
export function todayISO(): string {
  return toLocalISODate(new Date());
}

/** Danh sách ngày ISO (inclusive) từ start đến end — dùng cho shift_dates.
 * Khôi phục 2026-08-27 (phản hồi người dùng: DateRangePicker thật — commit
 * 440b351c6 — đã có nhưng chưa từng lên `main`, bị rớt lại khi PR #660 theo
 * kiến trúc day-chip của main; nối lại đây với marker "custom"/deadlineTime
 * đã thêm sau đó). */
export function daysInRange(start: string, end: string): string[] {
  const days: string[] = [];
  const cur = new Date(`${start}T00:00:00`);
  const last = new Date(`${end}T00:00:00`);
  while (cur <= last) {
    days.push(toLocalISODate(cur));
    cur.setDate(cur.getDate() + 1);
  }
  return days;
}

function formatDateTime(iso: string): string {
  return new Date(iso).toLocaleString("vi-VN", {
    day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit",
  });
}

let blockIdSeq = 0;
function nextBlockId(): string {
  blockIdSeq += 1;
  return `block-${blockIdSeq}`;
}

/**
 * BOT-004(a)-class hardening (issue #715, PR chưa merge): trước bản sửa này
 * `bulkCreate`/`approveTask`/`bulkApproveTasks` (useManagerTasks.ts) không có
 * `onError`, và các handler gọi `.mutateAsync` KHÔNG có try/catch — khi API
 * lỗi (network/422/500), promise reject không ai bắt, UI không hiện gì
 * (giống hệt mô tả "ghi thất bại mà UI hoàn toàn im lặng, chỉ thấy qua F12").
 * Bóc message VI theo cùng convention TenantUserManagement.tsx (FastAPI
 * validation `detail` có thể là mảng [{msg}], không phải string).
 */
function extractTaskErrorMessage(err: unknown, fallback: string): string {
  const detail = (err as { response?: { data?: { detail?: unknown } } })?.response?.data?.detail;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) {
    return detail.map((d) => (d as { msg?: unknown })?.msg ?? String(d)).join(", ");
  }
  if (detail && typeof detail === "object") return JSON.stringify(detail);
  return (err as { message?: string })?.message ?? fallback;
}

interface TaskBlock {
  id: string;
  title: string;
  description: string;
  selectedIds: Set<string>;
}

// ─────────────────────────────────────────────────────────────────────────
// Modal Giao việc
// ─────────────────────────────────────────────────────────────────────────

function GiaoViecModal({ onClose, onDone }: { onClose: () => void; onDone: (msg: string) => void }) {
  const [dateRange, setDateRange] = useState<DateRangeValue>(() => ({ start: todayISO(), end: todayISO() }));
  const [marker, setMarker] = useState<DeadlineMarker>("end");
  const [deadlineTime, setDeadlineTime] = useState("17:00");
  const [search, setSearch] = useState("");
  const [onlyToday, setOnlyToday] = useState(false);
  const [blocks, setBlocks] = useState<TaskBlock[]>(() => [
    { id: nextBlockId(), title: "", description: "", selectedIds: new Set() },
  ]);
  const [error, setError] = useState<string | null>(null);

  const { data: roster, isLoading: rosterLoading } = useTaskRoster(todayISO());
  const bulkCreate = useBulkCreateTasks();

  const selectedDays = useMemo(
    () => (dateRange.start && dateRange.end ? daysInRange(dateRange.start, dateRange.end) : []),
    [dateRange],
  );

  const filteredItems = useMemo(() => {
    const items = roster?.items ?? [];
    const term = search.trim().toLowerCase();
    return items.filter((item) => {
      if (onlyToday && !item.has_shift_today) return false;
      if (!term) return true;
      const nameMatch = item.full_name.toLowerCase().includes(term);
      const codeMatch = (item.employee_code ?? "").toLowerCase().includes(term);
      return nameMatch || codeMatch;
    });
  }, [roster, search, onlyToday]);

  const groups = useMemo(() => {
    const map = new Map<string, TaskRosterItem[]>();
    for (const item of filteredItems) {
      const key = item.work_area_name;
      if (!map.has(key)) map.set(key, []);
      map.get(key)!.push(item);
    }
    return Array.from(map.entries());
  }, [filteredItems]);

  const updateBlock = (id: string, patch: Partial<Omit<TaskBlock, "id">>) => {
    setBlocks((prev) => prev.map((b) => (b.id === id ? { ...b, ...patch } : b)));
  };

  const toggleBlockEmployee = (blockId: string, empId: string) => {
    setBlocks((prev) =>
      prev.map((b) => {
        if (b.id !== blockId) return b;
        const next = new Set(b.selectedIds);
        if (next.has(empId)) next.delete(empId);
        else next.add(empId);
        return { ...b, selectedIds: next };
      }),
    );
  };

  const toggleBlockGroup = (blockId: string, items: TaskRosterItem[]) => {
    // 2026-08-12: "Chọn cả nhóm" chỉ chọn NV có ca — NV chưa được phân ca bị
    // khoá chọn (xem lý do ở rosterItem), backend cũng chặn nếu lọt qua.
    const selectableIds = items.filter((i) => i.has_shift_today).map((i) => i.id);
    setBlocks((prev) =>
      prev.map((b) => {
        if (b.id !== blockId) return b;
        const allSelected = selectableIds.length > 0 && selectableIds.every((id) => b.selectedIds.has(id));
        const next = new Set(b.selectedIds);
        if (allSelected) selectableIds.forEach((id) => next.delete(id));
        else selectableIds.forEach((id) => next.add(id));
        return { ...b, selectedIds: next };
      }),
    );
  };

  const addBlock = () => {
    setBlocks((prev) => [...prev, { id: nextBlockId(), title: "", description: "", selectedIds: new Set() }]);
  };

  const removeBlock = (id: string) => {
    setBlocks((prev) => (prev.length <= 1 ? prev : prev.filter((b) => b.id !== id)));
  };

  const totalSelected = blocks.reduce((sum, b) => sum + b.selectedIds.size, 0);
  const totalTasks = totalSelected * selectedDays.length;

  const handleSubmit = async () => {
    setError(null);
    if (selectedDays.length === 0) {
      setError("Vui lòng chọn khoảng ngày.");
      return;
    }
    if (selectedDays.length > MAX_GIAO_VIEC_SPAN_DAYS) {
      setError(`Khoảng ngày tối đa ${MAX_GIAO_VIEC_SPAN_DAYS} ngày/lần — vui lòng chọn khoảng ngắn hơn.`);
      return;
    }
    if (marker === "custom" && !deadlineTime) {
      setError("Vui lòng nhập giờ hạn chót.");
      return;
    }
    const emptyTitleBlock = blocks.find((b) => !b.title.trim());
    if (emptyTitleBlock) {
      setError("Vui lòng nhập nội dung cho mọi công việc.");
      return;
    }
    const noEmployeeBlock = blocks.find((b) => b.selectedIds.size === 0);
    if (noEmployeeBlock) {
      setError(`Vui lòng chọn ít nhất 1 nhân viên cho việc "${noEmployeeBlock.title}".`);
      return;
    }

    const items: BulkTaskItemPayload[] = blocks.map((b) => ({
      title: b.title.trim(),
      description: b.description.trim() || undefined,
      assigned_to_ids: Array.from(b.selectedIds),
    }));

    try {
      const result = await bulkCreate.mutateAsync({
        items,
        shift_dates: selectedDays,
        deadline_marker: marker,
        deadline_time: marker === "custom" ? deadlineTime : undefined,
        requires_approval: true,
      });
      if (result.created.length > 0) {
        const msg =
          result.skipped.length > 0
            ? `Đã tạo ${result.created.length} nhiệm vụ (${result.skipped.length} bị bỏ qua).`
            : `Đã tạo ${result.created.length} nhiệm vụ.`;
        onDone(msg);
      } else {
        setError(result.skipped[0]?.error ?? "Không thể giao việc — vui lòng thử lại.");
      }
    } catch (err) {
      // BOT-004(a)-class (#715): request thất bại (network/422/500) — trước
      // đây không ai catch, modal đứng im không nói gì. Giờ hiện banner lỗi
      // sẵn có (dòng ~257) thay vì im lặng.
      setError(extractTaskErrorMessage(err, "Không thể giao việc — vui lòng thử lại."));
    }
  };

  return (
    <div className={styles.modalOverlay} onClick={onClose}>
      <div
        className={styles.modal}
        role="dialog"
        aria-modal="true"
        aria-labelledby="giaoViecTitle"
        onClick={(e) => e.stopPropagation()}
      >
        <h3 className={styles.modalTitle} id="giaoViecTitle">
          Giao việc
        </h3>

        {error && (
          <div className={`${styles.banner} ${styles.bannerError}`} role="alert">
            ⚠️ {error}
          </div>
        )}

        <label className={styles.modalLabel}>Ngày ca (chọn khoảng ngày)</label>
        <DateRangePicker
          value={dateRange}
          onChange={setDateRange}
          maxSpanDays={MAX_GIAO_VIEC_SPAN_DAYS}
          placeholder="Chọn khoảng ngày giao việc"
        />

        <label className={styles.modalLabel}>Hạn hoàn thành (áp dụng cho mọi ngày đã chọn)</label>
        <div className={styles.markerGroup}>
          {MARKER_OPTIONS.map((opt) => (
            <button
              key={opt.value}
              type="button"
              className={`${styles.markerBtn} ${marker === opt.value ? styles.markerBtnActive : ""}`}
              onClick={() => setMarker(opt.value)}
            >
              {opt.label}
            </button>
          ))}
        </div>
        {marker === "custom" && (
          <TimeTextInput
            className={styles.modalInput}
            style={{ marginTop: "var(--nqh-space-2)", maxWidth: 160 }}
            value={deadlineTime}
            onChange={(e) => setDeadlineTime(e.target.value)}
            aria-label="Giờ hạn chót"
          />
        )}

        <div className={styles.rosterFilterRow}>
          <input
            type="text"
            className={styles.modalInput}
            placeholder="Tìm theo tên hoặc Mã NV..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <label className={styles.rosterToggle}>
            <input
              type="checkbox"
              checked={onlyToday}
              onChange={(e) => setOnlyToday(e.target.checked)}
            />
            Chỉ người có ca hôm nay
          </label>
        </div>

        {blocks.map((block, idx) => {
          const selectedItems = (roster?.items ?? []).filter((item) => block.selectedIds.has(item.id));
          return (
            <div key={block.id} className={styles.taskBlock}>
              <div className={styles.taskBlockHead}>
                <span className={styles.taskBlockLabel}>Công việc {idx + 1}</span>
                {blocks.length > 1 && (
                  <button
                    type="button"
                    className={styles.chipRemove}
                    onClick={() => removeBlock(block.id)}
                    aria-label={`Xoá công việc ${idx + 1}`}
                  >
                    Xoá việc này
                  </button>
                )}
              </div>

              <textarea
                className={styles.modalTextarea}
                value={block.title}
                onChange={(e) => updateBlock(block.id, { title: e.target.value })}
                placeholder="Ví dụ: Dọn dẹp quầy lễ tân, kiểm kho nguyên liệu..."
              />
              <textarea
                className={styles.modalTextarea}
                value={block.description}
                onChange={(e) => updateBlock(block.id, { description: e.target.value })}
                placeholder="Mô tả thêm (không bắt buộc)"
              />

              <label className={styles.modalLabel}>
                Chọn nhân viên ({block.selectedIds.size} đã chọn)
              </label>
              {selectedItems.length > 0 && (
                <div className={styles.selectedChips}>
                  {selectedItems.map((item) => (
                    <span key={item.id} className={styles.chip}>
                      {item.full_name}
                      <button
                        type="button"
                        className={styles.chipRemove}
                        onClick={() => toggleBlockEmployee(block.id, item.id)}
                        aria-label={`Bỏ chọn ${item.full_name}`}
                      >
                        ×
                      </button>
                    </span>
                  ))}
                </div>
              )}

              <div className={styles.rosterList}>
                {rosterLoading ? (
                  <div className={styles.loadingState}>Đang tải danh sách...</div>
                ) : groups.length === 0 ? (
                  <div className={styles.emptyState}>Không tìm thấy nhân viên phù hợp.</div>
                ) : (
                  groups.map(([groupName, items]) => {
                    const selectableItems = items.filter((i) => i.has_shift_today);
                    const allSelected =
                      selectableItems.length > 0 &&
                      selectableItems.every((i) => block.selectedIds.has(i.id));
                    return (
                      <div key={groupName}>
                        <div className={styles.rosterGroupHead}>
                          <span>{groupName} ({items.length})</span>
                          <label className={styles.rosterToggle} onClick={(e) => e.stopPropagation()}>
                            <input
                              type="checkbox"
                              checked={allSelected}
                              disabled={selectableItems.length === 0}
                              onChange={() => toggleBlockGroup(block.id, items)}
                            />
                            Chọn cả nhóm
                          </label>
                        </div>
                        {items.map((item) => {
                          // 2026-08-12 (user báo lỗi): NV chưa được phân ca hôm nay
                          // không được giao việc — khoá chọn thay vì để giao rồi
                          // backend mới từ chối.
                          const disabled = !item.has_shift_today;
                          return (
                            <div
                              key={item.id}
                              className={`${styles.rosterItem} ${disabled ? styles.rosterItemDisabled : ""}`}
                              onClick={() => !disabled && toggleBlockEmployee(block.id, item.id)}
                              title={disabled ? "Chưa được phân ca hôm nay — không thể giao việc" : undefined}
                            >
                              <input
                                type="checkbox"
                                checked={block.selectedIds.has(item.id)}
                                disabled={disabled}
                                onChange={() => toggleBlockEmployee(block.id, item.id)}
                                onClick={(e) => e.stopPropagation()}
                              />
                              <span className={styles.rosterItemName}>
                                {item.employee_code ? `[${item.employee_code}] ` : ""}
                                {item.full_name}
                              </span>
                              <span className={styles.rosterItemMeta}>
                                {item.position ?? ""}
                                {item.has_shift_today && item.shift
                                  ? ` · ca ${item.shift.start_time}-${item.shift.end_time}`
                                  : " · chưa được phân ca hôm nay"}
                              </span>
                            </div>
                          );
                        })}
                      </div>
                    );
                  })
                )}
              </div>
            </div>
          );
        })}

        <button type="button" className={`${styles.btn} ${styles.btnSecondary}`} onClick={addBlock}>
          + Thêm việc khác
        </button>

        <div className={styles.modalActions}>
          <button type="button" className={`${styles.btn} ${styles.btnSecondary}`} onClick={onClose}>
            Hủy
          </button>
          <button
            type="button"
            className={`${styles.btn} ${styles.btnPrimary}`}
            onClick={handleSubmit}
            disabled={bulkCreate.isPending}
          >
            {bulkCreate.isPending ? "Đang giao việc..." : `Giao việc (${totalTasks} nhiệm vụ)`}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// Modal Từ chối (lý do bắt buộc)
// ─────────────────────────────────────────────────────────────────────────

function RejectModal({
  taskTitle,
  onCancel,
  onConfirm,
  isPending,
}: {
  taskTitle: string;
  onCancel: () => void;
  onConfirm: (reason: string) => void;
  isPending: boolean;
}) {
  const [reason, setReason] = useState("");
  return (
    <div className={styles.modalOverlay} onClick={onCancel}>
      <div className={styles.modal} role="dialog" aria-modal="true" onClick={(e) => e.stopPropagation()}>
        <h3 className={styles.modalTitle}>Từ chối nhiệm vụ</h3>
        <p>Bạn đang từ chối: <strong>{taskTitle}</strong></p>
        <label className={styles.modalLabel} htmlFor="reject-reason">Lý do từ chối (bắt buộc)</label>
        <textarea
          id="reject-reason"
          className={styles.modalTextarea}
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder="Vì sao chưa đạt? NV sẽ thấy lý do này và làm lại."
        />
        <div className={styles.modalActions}>
          <button type="button" className={`${styles.btn} ${styles.btnSecondary}`} onClick={onCancel}>
            Hủy
          </button>
          <button
            type="button"
            className={`${styles.btn} ${styles.btnDanger}`}
            disabled={!reason.trim() || isPending}
            onClick={() => onConfirm(reason.trim())}
          >
            {isPending ? "Đang xử lý..." : "Xác nhận từ chối"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// Lightbox — zoom ảnh bằng chứng
// ─────────────────────────────────────────────────────────────────────────

function PhotoLightbox({ src, onClose }: { src: string; onClose: () => void }) {
  return (
    <div className={styles.lightboxOverlay} onClick={onClose} role="dialog" aria-modal="true">
      <button type="button" className={styles.lightboxClose} onClick={onClose} aria-label="Đóng">
        ×
      </button>
      <img src={src} alt="Bằng chứng phóng to" className={styles.lightboxImage} onClick={(e) => e.stopPropagation()} />
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// Tổng hợp nhiệm vụ theo NV (ai làm nhiều/ít cuối tháng)
// ─────────────────────────────────────────────────────────────────────────

function monthStartISO(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-01`;
}

function EmployeeSummarySection() {
  const [dateFrom, setDateFrom] = useState(monthStartISO());
  const [dateTo, setDateTo] = useState(todayISO());
  const { data, isLoading, error } = useEmployeeTaskSummary(dateFrom, dateTo);

  return (
    <div className={styles.listWrap}>
      <h3 className={styles.listWrapTitle}>Tổng hợp nhiệm vụ theo nhân viên</h3>
      <div className={styles.summaryFilterRow}>
        <label className={styles.summaryFilterLabel}>
          Từ ngày
          <input type="date" className={styles.modalInput} value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} />
        </label>
        <label className={styles.summaryFilterLabel}>
          Đến ngày
          <input type="date" className={styles.modalInput} value={dateTo} onChange={(e) => setDateTo(e.target.value)} />
        </label>
      </div>

      {isLoading ? (
        <div className={styles.loadingState}>Đang tải...</div>
      ) : error ? (
        <div className={styles.errorState}>⚠️ Lỗi tải dữ liệu.</div>
      ) : !data || data.items.length === 0 ? (
        <div className={styles.emptyState}>Không có nhiệm vụ nào trong khoảng ngày đã chọn.</div>
      ) : (
        <div className={styles.summaryTableWrap}>
          <table className={styles.summaryTable}>
            <thead>
              <tr>
                <th>Nhân viên</th>
                <th>Tổng</th>
                <th>Hoàn thành</th>
                <th>Đã duyệt</th>
                <th>Bị từ chối</th>
                {/* bot-071 (GH#891): đổi nhãn — pending_confirm đếm ASSIGNED/ACKNOWLEDGED/
                    IN_PROGRESS/OVERDUE (chưa báo xong việc), không phải "chưa nhận việc" như
                    nhãn cũ "Chưa xác nhận" ngụ ý (NV bấm "Đã nhận" không làm cột này giảm) */}
                <th>Chưa hoàn thành</th>
                <th>Chờ duyệt</th>
              </tr>
            </thead>
            <tbody>
              {data.items.map((row) => (
                <tr key={row.tenant_user_id}>
                  <td>{row.full_name}</td>
                  <td>{row.total}</td>
                  <td>{row.completed}</td>
                  <td>{row.approved}</td>
                  <td>{row.rejected}</td>
                  <td>{row.pending_confirm}</td>
                  <td>{row.pending_approval}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// Lịch sử nhiệm vụ — Phần 1: Chưa hoàn thành (giao lúc) / Phần 2: Đã hoàn thành (xong lúc)
// ─────────────────────────────────────────────────────────────────────────

const TASK_STATUS_LABELS_VI: Record<string, string> = {
  ASSIGNED: "Đã giao",
  ACKNOWLEDGED: "NV đã xác nhận",
  IN_PROGRESS: "Đang làm",
  OVERDUE: "Quá hạn",
  ESCALATED: "Đã leo thang",
  CLOSED: "Đã đóng (quản lý)",
  REASSIGNED: "Đã giao lại",
  COMPLETED: "Hoàn thành",
};

function TaskHistoryRow({ item, timeLabel, timeValue }: { item: TaskHistoryItem; timeLabel: string; timeValue: string | null }) {
  return (
    <div className={styles.taskRow}>
      <div className={styles.taskRowHead}>
        <div>
          <div className={styles.taskTitle}>{item.title}</div>
          <div className={styles.taskMeta}>
            {timeLabel} {timeValue ? formatDateTime(timeValue) : "—"}
          </div>
        </div>
        <span className={styles.statusChip}>{TASK_STATUS_LABELS_VI[item.status] ?? item.status}</span>
      </div>
      {item.description && <div className={styles.taskDesc}>{item.description}</div>}
    </div>
  );
}

function TaskHistorySection() {
  const [dateFrom, setDateFrom] = useState(monthStartISO());
  const [dateTo, setDateTo] = useState(todayISO());
  const { data, isLoading, error } = useTaskHistory(dateFrom, dateTo);

  const items = data?.items ?? [];
  // Chỉ COMPLETED là NV thật sự làm xong; 7 trạng thái còn lại (kể cả
  // ESCALATED/CLOSED/REASSIGNED — bế tắc/xử lý tay) đều tính "chưa hoàn thành"
  // theo quyết định của @cto (2026-08-18).
  const incomplete = items.filter((i) => i.status !== "COMPLETED");
  const completed = items.filter((i) => i.status === "COMPLETED");

  return (
    <div className={styles.listWrap}>
      <h3 className={styles.listWrapTitle}>Lịch sử nhiệm vụ</h3>
      <div className={styles.summaryFilterRow}>
        <label className={styles.summaryFilterLabel}>
          Từ ngày
          <input type="date" className={styles.modalInput} value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} />
        </label>
        <label className={styles.summaryFilterLabel}>
          Đến ngày
          <input type="date" className={styles.modalInput} value={dateTo} onChange={(e) => setDateTo(e.target.value)} />
        </label>
      </div>

      {isLoading ? (
        <div className={styles.loadingState}>Đang tải...</div>
      ) : error ? (
        <div className={styles.errorState}>⚠️ Lỗi tải dữ liệu.</div>
      ) : items.length === 0 ? (
        <div className={styles.emptyState}>Không có nhiệm vụ nào trong khoảng ngày đã chọn.</div>
      ) : (
        <>
          <h4 className={styles.historySubTitle}>Chưa hoàn thành ({incomplete.length})</h4>
          {incomplete.length === 0 ? (
            <div className={styles.emptyState}>Không có nhiệm vụ nào đang treo.</div>
          ) : (
            incomplete.map((item) => (
              <TaskHistoryRow key={item.id} item={item} timeLabel="Giao lúc" timeValue={item.created_at} />
            ))
          )}

          <h4 className={styles.historySubTitle}>Đã hoàn thành ({completed.length})</h4>
          {completed.length === 0 ? (
            <div className={styles.emptyState}>Không có nhiệm vụ nào đã hoàn thành.</div>
          ) : (
            completed.map((item) => (
              <TaskHistoryRow key={item.id} item={item} timeLabel="Xong lúc" timeValue={item.completed_at} />
            ))
          )}
        </>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// Page
// ─────────────────────────────────────────────────────────────────────────

const ManagerTasksPage: React.FC = () => {
  const [showGiaoViec, setShowGiaoViec] = useState(false);
  const [rejectTarget, setRejectTarget] = useState<{ id: string; title: string } | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  // BOT-004(a)-class hardening (#715): duyệt/từ chối/duyệt-hàng-loạt trước
  // đây không có nơi hiện lỗi — xem extractTaskErrorMessage ở trên.
  const [actionError, setActionError] = useState<string | null>(null);
  const [lightboxSrc, setLightboxSrc] = useState<string | null>(null);
  const [showSummary, setShowSummary] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [selectedApproveIds, setSelectedApproveIds] = useState<Set<string>>(new Set());

  const { data, isLoading, error, refetch } = usePendingApprovalTasks();
  const approveTask = useApproveTask();
  const bulkApproveTasks = useBulkApproveTasks();

  const items = data?.items ?? [];

  const notify = (msg: string) => {
    setSuccessMessage(msg);
    setShowGiaoViec(false);
    setTimeout(() => setSuccessMessage(null), 4000);
    refetch();
  };

  const notifyError = (msg: string) => {
    setActionError(msg);
    setTimeout(() => setActionError(null), 6000);
  };

  const handleApprove = async (taskId: string) => {
    try {
      await approveTask.mutateAsync({ taskId, payload: { approved: true } });
      notify("Đã duyệt nhiệm vụ.");
    } catch (err) {
      // BOT-004(a)-class (#715): trước đây không catch — nút "Duyệt" chỉ tắt
      // spinner rồi im lặng khi lỗi, người dùng tưởng đã duyệt xong.
      notifyError(extractTaskErrorMessage(err, "Không duyệt được nhiệm vụ — vui lòng thử lại."));
    }
  };

  const handleReject = async (reason: string) => {
    if (!rejectTarget) return;
    try {
      await approveTask.mutateAsync({
        taskId: rejectTarget.id,
        payload: { approved: false, notes: reason },
      });
      setRejectTarget(null);
      notify("Đã từ chối nhiệm vụ — nhân viên sẽ làm lại.");
    } catch (err) {
      notifyError(extractTaskErrorMessage(err, "Không từ chối được nhiệm vụ — vui lòng thử lại."));
    }
  };

  const toggleSelectApprove = (taskId: string) => {
    setSelectedApproveIds((prev) => {
      const next = new Set(prev);
      if (next.has(taskId)) next.delete(taskId);
      else next.add(taskId);
      return next;
    });
  };

  const allApproveSelected = items.length > 0 && items.every((t) => selectedApproveIds.has(t.id));
  const toggleSelectAllApprove = () => {
    setSelectedApproveIds(allApproveSelected ? new Set() : new Set(items.map((t) => t.id)));
  };

  const handleBulkApprove = async () => {
    const count = selectedApproveIds.size;
    if (count === 0) return;
    try {
      await bulkApproveTasks.mutateAsync(Array.from(selectedApproveIds));
      setSelectedApproveIds(new Set());
      notify(`Đã duyệt ${count} nhiệm vụ.`);
    } catch (err) {
      notifyError(extractTaskErrorMessage(err, "Không duyệt được — vui lòng thử lại."));
    }
  };

  return (
    <div>
      <div className={styles.pageHead}>
        <h2 className={styles.pageHeadTitle}>Giao việc</h2>
        <div className={styles.pageHeadActions}>
          <button
            type="button"
            className={`${styles.btn} ${styles.btnSecondary}`}
            onClick={() => setShowSummary((v) => !v)}
          >
            {showSummary ? "Ẩn tổng hợp" : "Tổng hợp nhiệm vụ"}
          </button>
          <button
            type="button"
            className={`${styles.btn} ${styles.btnSecondary}`}
            onClick={() => setShowHistory((v) => !v)}
          >
            {showHistory ? "Ẩn lịch sử" : "Lịch sử nhiệm vụ"}
          </button>
          <button type="button" className={`${styles.btn} ${styles.btnPrimary}`} onClick={() => setShowGiaoViec(true)}>
            + Giao việc
          </button>
        </div>
      </div>

      {successMessage && (
        <div className={`${styles.banner} ${styles.bannerSuccess}`} role="status">
          ✅ {successMessage}
        </div>
      )}
      {actionError && (
        <div className={`${styles.banner} ${styles.bannerError}`} role="alert">
          ⚠️ {actionError}
        </div>
      )}

      {showSummary && <EmployeeSummarySection />}
      {showHistory && <TaskHistorySection />}

      <div className={styles.listWrap}>
        <div className={styles.taskRowHead}>
          <h3 className={styles.listWrapTitle}>
            Chờ duyệt {data ? `(${data.total})` : ""}
          </h3>
          {items.length > 0 && (
            <button
              type="button"
              className={`${styles.btn} ${styles.btnSuccess}`}
              disabled={selectedApproveIds.size === 0 || bulkApproveTasks.isPending}
              onClick={handleBulkApprove}
            >
              ✓ Duyệt đã chọn ({selectedApproveIds.size})
            </button>
          )}
        </div>

        {isLoading ? (
          <div className={styles.loadingState}>Đang tải...</div>
        ) : error ? (
          <div className={styles.errorState}>
            ⚠️ Lỗi tải dữ liệu. <button type="button" onClick={() => refetch()}>Thử lại</button>
          </div>
        ) : items.length === 0 ? (
          <div className={styles.emptyState}>Không có nhiệm vụ nào đang chờ duyệt.</div>
        ) : (
          <>
            <label className={styles.selectAllRow}>
              <input type="checkbox" checked={allApproveSelected} onChange={toggleSelectAllApprove} />
              Chọn tất cả
            </label>
            {items.map((task) => {
            const photos = parseTaskPhotos(task.completion_photo_url);
            return (
              <div key={task.id} className={styles.taskRow}>
                <div className={styles.taskRowHead}>
                  <label className={styles.selectRow}>
                    <input
                      type="checkbox"
                      checked={selectedApproveIds.has(task.id)}
                      onChange={() => toggleSelectApprove(task.id)}
                    />
                  </label>
                  <div>
                    <div className={styles.taskTitle}>{task.title}</div>
                    <div className={styles.taskMeta}>
                      Đã xong lúc {task.completed_at ? formatDateTime(task.completed_at) : "—"} · Hạn: {formatDateTime(task.deadline)}
                    </div>
                  </div>
                  <span className={styles.statusChip}>Chờ duyệt</span>
                </div>
                {task.description && <div className={styles.taskDesc}>{task.description}</div>}
                {photos.length > 0 && (
                  <div className={styles.taskPhotos}>
                    {photos.map((src, idx) => (
                      <img
                        key={idx}
                        src={src}
                        alt={`Bằng chứng ${idx + 1}`}
                        className={styles.taskPhoto}
                        onClick={() => setLightboxSrc(src)}
                      />
                    ))}
                  </div>
                )}
                <div className={styles.taskActions}>
                  <button
                    type="button"
                    className={`${styles.btn} ${styles.btnSuccess}`}
                    disabled={approveTask.isPending}
                    onClick={() => handleApprove(task.id)}
                  >
                    ✓ Duyệt
                  </button>
                  <button
                    type="button"
                    className={`${styles.btn} ${styles.btnDanger}`}
                    disabled={approveTask.isPending}
                    onClick={() => setRejectTarget({ id: task.id, title: task.title })}
                  >
                    ✗ Từ chối
                  </button>
                </div>
              </div>
            );
            })}
          </>
        )}
      </div>

      {showGiaoViec && (
        <GiaoViecModal onClose={() => setShowGiaoViec(false)} onDone={notify} />
      )}

      {rejectTarget && (
        <RejectModal
          taskTitle={rejectTarget.title}
          onCancel={() => setRejectTarget(null)}
          onConfirm={handleReject}
          isPending={approveTask.isPending}
        />
      )}

      {lightboxSrc && <PhotoLightbox src={lightboxSrc} onClose={() => setLightboxSrc(null)} />}
    </div>
  );
};

export default ManagerTasksPage;
